// Q12 S5 — NotificationsDrawer row component (Q12 D7a reshape).
// Outer <div role="button" tabIndex={0}> wraps the body click target;
// inner <button> elements handle ✓ (mark-read) + ✗ (dismiss) without
// nested-button HTML invalid. e.stopPropagation() on inner button
// clicks prevents the outer activate from firing.
//
// Visual state mirrors the pre-Q12 layout (left border accent for unread)
// per D13a — link-bearing and link-less rows look identical; the only
// difference is click behavior, handled by the parent drawer.

import type { JSX, KeyboardEvent } from "react";
import type { Notification } from "./NotificationsDrawer.js";

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

interface Props {
  item: Notification;
  onActivate: (id: string) => void;
  onMarkRead: (id: string) => void;
  onDismiss: (id: string) => void;
}

export function NotificationRow({ item, onActivate, onMarkRead, onDismiss }: Props): JSX.Element {
  const unread = item.read_at === null;
  const accent = unread ? "border-blue-500" : "border-slate-200";
  const handleKey = (e: KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onActivate(item._id);
    }
  };
  const stop = (handler: (id: string) => void) => (e: React.MouseEvent): void => {
    e.stopPropagation();
    handler(item._id);
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onActivate(item._id)}
      onKeyDown={handleKey}
      className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50 transition-colors border-l-4 pl-3 cursor-pointer focus:outline-none focus:ring-2 focus:ring-navy-300 flex items-start gap-2 ${accent}`}
      aria-label={item.title}
    >
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-800 leading-snug">{item.title}</p>
        <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{item.body}</p>
        <p className="text-[10px] text-slate-400 mt-1">{relativeTime(item.created_at)}</p>
      </div>
      <div className="flex flex-col gap-1 shrink-0">
        <button
          type="button"
          aria-label="Mark as read"
          onClick={stop(onMarkRead)}
          disabled={!unread}
          className="text-xs text-slate-400 hover:text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed leading-none px-1"
        >
          ✓
        </button>
        <button
          type="button"
          aria-label="Dismiss"
          onClick={stop(onDismiss)}
          className="text-xs text-slate-400 hover:text-red-600 leading-none px-1"
        >
          ✗
        </button>
      </div>
    </div>
  );
}
